/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ButtonHTMLAttributes } from 'react';
import './Button.css';

type Variant = 'primary' | 'secondary' | 'text' | 'ghost' | 'accent';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    children?: React.ReactNode;
    type?: 'button' | 'submit' | 'reset';
    variant?: Variant;
    size?: Size;
    loading?: boolean;
    disabled?: boolean;
    startIcon?: React.ReactNode | any;
    endIcon?: React.ReactNode;
    fullWidth?: boolean;
    className?: string;
    ariaLabel?: string;
}

const Button: React.FC<ButtonProps> = ({
    children,
    type = 'button',
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled = false,
    startIcon,
    endIcon,
    fullWidth = false,
    className = '',
    ariaLabel,
    ...rest
}) => {
    const isDisabled = disabled || loading;
    const classes = [
        'btn',
        `btn-${variant}`,
        `btn-${size}`,
        fullWidth ? 'btn-block' : '',
        loading ? 'btn-loading' : '',
        className
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <button
            type={type}
            className={classes}
            disabled={isDisabled}
            aria-busy={loading || undefined}
            aria-label={ariaLabel}
            {...rest}
        >
            {loading && (
                <span className="btn-spinner" aria-hidden>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <circle
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeDasharray="32"
                            strokeDashoffset="32"
                        >
                            <animate
                                attributeName="stroke-dashoffset"
                                dur="2s"
                                values="32;0"
                                repeatCount="indefinite"
                            />
                        </circle>
                    </svg>
                </span>
            )}
            {startIcon && typeof startIcon !== 'string' && (
                <span className="btn-icon btn-icon--start">{startIcon}</span>
            )}
            {children && <span className="btn-label">{children}</span>}
            {endIcon && <span className="btn-icon btn-icon--end">{endIcon}</span>}
        </button>
    );
};

export default Button;
